import React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs';
import styled, { keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import useReportDataContext from '../../hooks/useReportDataContext';
import type { RootState } from '../../../../state/store';

const ColumnFtsFilter = ({ headerName, filterKey }: { headerName: any; filterKey: string }) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const { handleFtsFilterChange, selectedFtsFilters } = useReportDataContext();
  const [inputText, setInputText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownToggleRef = useRef<HTMLElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  // Check if this filter has an active FTS filter
  const currentFtsFilter = useMemo(() => {
    return selectedFtsFilters.find((filter) => filter.key === filterKey) || null;
  }, [selectedFtsFilters, filterKey]);

  // Initialize input text with current filter when dropdown opens
  useEffect(() => {
    if (isOpen && currentFtsFilter) {
      setInputText(currentFtsFilter.value);
    } else if (!isOpen) {
      setInputText('');
    }
  }, [isOpen, currentFtsFilter]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
  };

  const handleSubmit = () => {
    if (inputText.trim().length > 0) {
      handleFtsFilterChange(filterKey, inputText.trim());
      setInputText('');
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setInputText('');
    handleFtsFilterChange(filterKey, '');
    setIsOpen(false);
  };

  const handleCancel = () => {
    setInputText('');
    setIsOpen(false);
  };

  // Handle dropdown toggle with position calculation
  const handleToggle = (show: boolean) => {
    if (show && dropdownToggleRef.current) {
      const rect = dropdownToggleRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left
      });
    }
    setIsOpen(show);
  };

  // Handle position updates on scroll and click outside
  useEffect(() => {
    const updatePosition = () => {
      if (dropdownToggleRef.current && isOpen) {
        const rect = dropdownToggleRef.current.getBoundingClientRect();
        const newPosition = {
          top: rect.bottom,
          left: rect.left
        };
        setDropdownPosition(newPosition);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on toggle or dropdown content
      if (
        (dropdownToggleRef.current && dropdownToggleRef.current.contains(target)) ||
        (dropdownMenuRef.current && dropdownMenuRef.current.contains(target))
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleScroll = () => {
      updatePosition();
    };

    const handleResize = () => {
      updatePosition();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      // Listen for scroll events on all possible containers
      document.addEventListener('scroll', handleScroll, true);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      // Also try to find and listen to ag-Grid specific scroll containers
      const agGridViewports = document.querySelectorAll(
        '.ag-body-viewport, .ag-center-cols-viewport, .ag-body-horizontal-scroll-viewport'
      );
      agGridViewports.forEach((viewport) => {
        viewport.addEventListener('scroll', handleScroll);
      });

      // Update position immediately when dropdown opens
      updatePosition();

      // Cleanup function
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);

        agGridViewports.forEach((viewport) => {
          viewport.removeEventListener('scroll', handleScroll);
        });
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputText.trim().length > 0) {
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const hasActiveFilter = currentFtsFilter && currentFtsFilter.value.length > 0;
  const hasChanges = inputText.trim() !== (currentFtsFilter?.value || '');

  return (
    <StyledDropdownWrapper>
      <div className="column-fts-filter">
        <span
          ref={dropdownToggleRef}
          className={`filter-toggle ${hasActiveFilter ? 'has-filters' : ''}`}
          onClick={() => handleToggle(!isOpen)}
        >
          <span className="header-text">{headerName}</span>
          <BsSearch className="search-icon" />
          {hasActiveFilter && <span className="filter-indicator">●</span>}
        </span>

        {isOpen &&
          createPortal(
            <StyledPortalDropdown
              ref={dropdownMenuRef}
              $theme={theme}
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 999,
                minWidth: '320px',
                maxWidth: '400px',
                padding: 0,
                overflow: 'hidden'
              }}
            >
              <div className="filter-header">
                <h6 className="filter-title">{headerName}</h6>
                {hasActiveFilter && (
                  <Button variant="link" size="sm" onClick={handleClear} className="clear-btn">
                    Clear
                  </Button>
                )}
              </div>

              <div className="search-container">
                <div className="search-input-wrapper">
                  <BsSearch className="search-input-icon" />
                  <Form.Control
                    type="text"
                    placeholder={`Search ${headerName.toLowerCase()}...`}
                    value={inputText}
                    onChange={handleOnChange}
                    onKeyDown={handleKeyDown}
                    className="search-input"
                    autoComplete="off"
                    autoFocus
                  />
                </div>
              </div>

              <div className="filter-content">
                {currentFtsFilter && (
                  <div className="current-filter">
                    <small className="current-filter-label">Current search:</small>
                    <div className="current-filter-value">&quot;{currentFtsFilter.value}&quot;</div>
                  </div>
                )}

                <div className="action-buttons">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCancel}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!inputText.trim() || !hasChanges}
                    className="apply-button"
                  >
                    Search
                  </Button>
                </div>
              </div>
            </StyledPortalDropdown>,
            document.body
          )}
      </div>
    </StyledDropdownWrapper>
  );
};

export default ColumnFtsFilter;

const slideInScale = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95) translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(13, 138, 106, 0.25);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(13, 138, 106, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(13, 138, 106, 0);
  }
`;

// Styled component for portal dropdown
const StyledPortalDropdown = styled.div<{ $theme: string }>`
  position: fixed;
  z-index: 999;
  min-width: 320px;
  max-width: 400px;
  padding: 0;
  border: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
  border-radius: 12px;
  background: ${({ $theme }) => ($theme === 'dark' ? '#1a1a1a' : '#ffffff')};
  box-shadow: 0 4px 15px rgba(0, 0, 0, ${({ $theme }) => ($theme === 'dark' ? '0.3' : '0.1')});
  animation: ${slideInScale} 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: ${({ $theme }) => ($theme === 'dark' ? '#2d2d2d' : '#d8edea')};
    border-bottom: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};

    .filter-title {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: ${({ $theme }) => ($theme === 'dark' ? '#ffffff' : '#374151')};
    }

    .clear-btn {
      padding: 4px 8px;
      font-size: 0.8rem;
      text-decoration: none;
      color: #0d8a6c;

      &:hover {
        text-decoration: underline;
        color: #0d8a6c;
      }
    }
  }

  .search-container {
    padding: 16px 20px;
    background: ${({ $theme }) => ($theme === 'dark' ? '#1a1a1a' : '#ffffff')};
    border-bottom: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};

    .search-input-wrapper {
      position: relative;

      .search-input-icon {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        font-size: 0.9rem;
        color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e580' : '#49505780')};
        z-index: 1;
      }

      .search-input {
        width: 100%;
        padding: 12px 16px 12px 35px;
        border: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
        border-radius: 8px;
        font-size: 0.9rem;
        color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e5' : '#495057')};
        background: ${({ $theme }) => ($theme === 'dark' ? '#2d2d2d' : '#f8f9fa')};
        transition: all 0.2s ease;

        &::placeholder {
          color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e560' : '#49505760')};
        }

        &:focus {
          outline: none;
          border-color: #0d8a6c;
          box-shadow: 0 0 0 3px #0d8a6c20;
        }
      }
    }
  }

  .filter-content {
    .current-filter {
      padding: 12px 20px;
      background: ${({ $theme }) => ($theme === 'dark' ? '#0d8a6c15' : '#0d8a6c08')};
      border-bottom: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};

      .current-filter-label {
        display: block;
        font-size: 0.8rem;
        color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e580' : '#49505780')};
        margin-bottom: 4px;
      }

      .current-filter-value {
        font-size: 0.9rem;
        color: #0d8a6c;
        font-weight: 500;
      }
    }

    .action-buttons {
      display: flex;
      gap: 12px;
      padding: 16px 20px;
      background: ${({ $theme }) => ($theme === 'dark' ? '#1a1a1a' : '#ffffff')};

      .cancel-button {
        flex: 1;
        border-color: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
        color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e5' : '#495057')};
        background-color: ${({ $theme }) => ($theme === 'dark' ? '#2d2d2d' : '#ffffff')};

        &:hover {
          background: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#f8f9fa')};
          color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e5' : '#495057')};
          border-color: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
        }
      }

      .apply-button {
        flex: 2;
        background: #0d8a6c;
        border-color: #0d8a6c;
        font-weight: 600;
        color: #ffffff;

        &:hover:not(:disabled) {
          background: #0d8a6cdd;
          border-color: #0d8a6cdd;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px #0d8a6c40;
        }

        &:disabled {
          background: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
          border-color: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
          color: ${({ $theme }) => ($theme === 'dark' ? '#999999' : '#6c757d')};
          cursor: not-allowed;
        }
      }
    }
  }
`;

const StyledDropdownWrapper = styled.span`
  .column-fts-filter {
    .filter-toggle {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: 8px;
      background: ${({ theme }) => theme.dropdown.background_color};
      color: ${({ theme }) => theme.text};
      border: 1px solid ${({ theme }) => theme.border_color};
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      user-select: none;

      &:hover {
        background: ${({ theme }) => theme.button_plain.hover};
        color: ${({ theme }) => theme.button_plain.hover_text};
        border-color: ${({ theme }) => theme.primary_color};
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      &.has-filters {
        background: ${({ theme }) => theme.primary_color}15;
        border-color: ${({ theme }) => theme.primary_color};
        color: ${({ theme }) => theme.primary_color};

        .search-icon {
          color: ${({ theme }) => theme.primary_color};
          animation: ${pulse} 2s infinite;
        }
      }

      .header-text {
        font-weight: 500;
        font-size: 0.9rem;
      }

      .search-icon {
        margin-left: 8px;
        transition: all 0.3s ease;
        font-size: 0.9rem;
      }

      .filter-indicator {
        position: absolute;
        top: -3px;
        right: -3px;
        background: ${({ theme }) => theme.primary_color};
        color: ${({ theme }) => theme.primary_color};
        border-radius: 50%;
        width: 8px;
        height: 8px;
        font-size: 6px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
    }

    .filter-dropdown-menu {
      position: fixed;
      z-index: 99999;
      min-width: 320px;
      max-width: 400px;
      padding: 0;
      border: 1px solid ${({ theme }) => theme.border_color};
      border-radius: 12px;
      background: ${({ theme }) => theme.dropdown.background_color};
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      animation: ${slideInScale} 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;

      .filter-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: ${({ theme }) => theme.widget.tableHeader};
        border-bottom: 1px solid ${({ theme }) => theme.border_color};

        .filter-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }

        .clear-btn {
          padding: 4px 8px;
          font-size: 0.8rem;
          text-decoration: none;
          color: ${({ theme }) => theme.primary_color};

          &:hover {
            text-decoration: underline;
            color: ${({ theme }) => theme.primary_color};
          }
        }
      }

      .search-container {
        padding: 16px 20px;
        background: ${({ theme }) => theme.widget.background};
        border-bottom: 1px solid ${({ theme }) => theme.border_color};

        .search-input-wrapper {
          position: relative;

          .search-input-icon {
            position: absolute;
            top: 50%;
            left: 12px;
            transform: translateY(-50%);
            font-size: 0.9rem;
            color: ${({ theme }) => theme.text}80;
            z-index: 1;
          }

          .search-input {
            width: 100%;
            padding: 12px 16px 12px 35px;
            border: 1px solid ${({ theme }) => theme.border_color};
            border-radius: 8px;
            font-size: 0.9rem;
            color: ${({ theme }) => theme.text};
            background: ${({ theme }) => theme.dropdown.background_color};
            transition: all 0.2s ease;

            &::placeholder {
              color: ${({ theme }) => theme.text}60;
            }

            &:focus {
              outline: none;
              border-color: ${({ theme }) => theme.primary_color};
              box-shadow: 0 0 0 3px ${({ theme }) => theme.primary_color}20;
            }
          }
        }
      }

      .filter-content {
        .current-filter {
          padding: 12px 20px;
          background: ${({ theme }) => theme.primary_color}08;
          border-bottom: 1px solid ${({ theme }) => theme.border_color};

          .current-filter-label {
            display: block;
            font-size: 0.8rem;
            color: ${({ theme }) => theme.text}80;
            margin-bottom: 4px;
          }

          .current-filter-value {
            font-size: 0.9rem;
            color: ${({ theme }) => theme.primary_color};
            font-weight: 500;
          }
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          background: ${({ theme }) => theme.widget.background};

          .cancel-button {
            flex: 1;
            border-color: ${({ theme }) => theme.border_color};
            color: ${({ theme }) => theme.text};

            &:hover {
              background: ${({ theme }) => theme.button_plain.hover};
              color: ${({ theme }) => theme.button_plain.hover_text};
              border-color: ${({ theme }) => theme.border_color};
            }
          }

          .apply-button {
            flex: 2;
            background: ${({ theme }) => theme.primary_color};
            border-color: ${({ theme }) => theme.primary_color};
            font-weight: 600;

            &:hover:not(:disabled) {
              background: ${({ theme }) => theme.primary_color}dd;
              border-color: ${({ theme }) => theme.primary_color}dd;
              transform: translateY(-1px);
              box-shadow: 0 4px 12px ${({ theme }) => theme.primary_color}40;
            }

            &:disabled {
              background: ${({ theme }) => theme.dropdown.disabled.background};
              border-color: ${({ theme }) => theme.dropdown.disabled.background};
              color: ${({ theme }) => theme.dropdown.disabled.color};
              cursor: not-allowed;
            }
          }
        }
      }
    }
  }
`;
